import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { shouldReportToSentry } from './lib/sentryErrorFilter';
import { showGlobalToast } from './utils/toastBridge';

type QueryRetryPolicy = boolean | number | ((failureCount: number, error: unknown) => boolean);

interface AppQueryClientOptions {
  queryRetry?: QueryRetryPolicy;
}

/** Shared query/mutation error boundary for every authenticated Valentine surface. */
export function createAppQueryClient({ queryRetry = 1 }: AppQueryClientOptions = {}) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: queryRetry,
      },
      mutations: { retry: false },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!shouldReportToSentry(error, query.queryKey, query.meta)) return;
        Sentry.captureException(error, {
          tags: { source: 'query' },
          contexts: { query: { key: JSON.stringify(query.queryKey) } },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const mutationKey = mutation.options.mutationKey;
        if (shouldReportToSentry(error, mutationKey, mutation.meta)) {
          Sentry.captureException(error, {
            tags: { source: 'mutation' },
            contexts: {
              mutation: { key: mutationKey ? JSON.stringify(mutationKey) : undefined },
            },
          });
        }

        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 409) return;
        const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        showGlobalToast(
          typeof detail === 'string' ? detail : 'Something went wrong. Please try again.',
          'error',
        );
      },
    }),
  });
}
